import {
  albumCollectionAddAlbum,
  albumCollectionByIdQuery,
  albumCollectionDeleteAlbum,
  albumCollectionOrderAlbums,
} from '@/entities/album-collections'
import { AlbumTypeEnum, albumNameUpdate } from '@/entities/albums'
import {
  type ChangeAlbumCollectionItemNumberModel,
  type TrackModel,
} from '@/shared/api'
import { ROUTES } from '@/shared/config'
import { navigateToFx } from '@/shared/lib/providers'
import { EmptyContent } from '@/shared/ui'
import { CloseOutlined, EditOutlined, LoadingOutlined } from '@ant-design/icons'
import { PlusOutlined } from '@ant-design/icons'
import { Button, Flex, Space, Table, Tooltip, Typography } from 'antd'
import { type TableProps } from 'antd/lib'
import { useUnit } from 'effector-react'

import {
  AlbumCollectionFormAlbumsGate,
  albumOrderModalOpened,
  tracksAlbumCollectionAlbumsQuery,
} from '../model/album-collection-form-albums.model'
import {
  $isAlbumSelectionModalOpen,
  albumSelectionModalOpened,
} from '../model/album-selection-modal.model'
import { AlbumReorderModal } from './AlbumReorderModal'
import { AlbumSelectionModal } from './AlbumSelectionModal'

export const AlbumCollectionFormAlbums = () => {
  const albumCollectionData = useUnit(albumCollectionByIdQuery.$data)
  const tracks = useUnit(tracksAlbumCollectionAlbumsQuery)

  const albumNameUpdateLatestParams = useUnit(albumNameUpdate.__.$latestParams)
  const albumNameUpdatePending = useUnit(albumNameUpdate.$pending)

  const updateAlbumName = useUnit(albumNameUpdate)
  // Selection modal
  const openAlbumSelectionModal = useUnit(albumSelectionModalOpened)
  const isAlbumSelectionModalOpen = useUnit($isAlbumSelectionModalOpen)
  // Order modal
  const openAlbumOrderModal = useUnit(albumOrderModalOpened)

  const addAlbumToAlbumCollection = useUnit(albumCollectionAddAlbum)
  const deleteAlbumFromAlbumCollection = useUnit(albumCollectionDeleteAlbum)
  const orderAlbumsInAlbumCollection = useUnit(albumCollectionOrderAlbums)

  const tracksTableColumns: TableProps<TrackModel>['columns'] = [
    {
      title: 'Наименование',
      width: '33%',
      dataIndex: 'name',
      key: 'name',
      render: (_, track) => {
        const route = ROUTES.AUDIO.EDIT.replace(':id', track.id)
        return (
          <span key={track.id}>
            <Typography.Link onClick={() => void navigateToFx({ to: route })}>
              {track.name}
            </Typography.Link>
          </span>
        )
      },
    },
    {
      title: 'Автор',
      width: '33%',
      dataIndex: 'author',
      key: 'author',
      render: (_, track) => {
        if (!track.authors) return ''
        const mainAuthors = track.authors.filter((a) => !a.isCoauthor)
        const coAuthors = track.authors.filter((a) => a.isCoauthor)
        const authors = [...mainAuthors, ...coAuthors]
        return (
          <>
            {authors.map((author, i) => {
              const isLast = i === authors.length - 1
              const route = ROUTES.AUTHORS.EDIT.replace(':id', author.id)

              return (
                <span key={author.id}>
                  <Typography.Link
                    onClick={() => void navigateToFx({ to: route })}
                  >
                    {author.nickname}
                  </Typography.Link>
                  {!isLast && ',\u00A0'}
                </span>
              )
            })}
          </>
        )
      },
    },
  ]

  return (
    <>
      {albumCollectionData !== null && albumCollectionData.albums && (
        <>
          <AlbumCollectionFormAlbumsGate
            AlbumCollectionInfo={albumCollectionData}
          />
          {isAlbumSelectionModalOpen && (
            <AlbumSelectionModal
              authorIds={
                albumCollectionData.authors.map((author) => author.id) ?? []
              }
              types={[AlbumTypeEnum.Podcast]}
              onApply={(id) => {
                addAlbumToAlbumCollection.start({
                  id: albumCollectionData.id,
                  data: {
                    albumIds: [id],
                  },
                })
              }}
              collectionId={albumCollectionData.id}
            />
          )}
          <AlbumReorderModal
            onApply={(orderedAlbums) => {
              if (!albumCollectionData || !albumCollectionData.albums) return

              const data: ChangeAlbumCollectionItemNumberModel[] = []
              orderedAlbums.forEach((album, newIndex) => {
                data.push({
                  albumId: album.id,
                  number: ++newIndex,
                })
              })
              orderAlbumsInAlbumCollection.start({
                id: albumCollectionData.id,
                data,
              })
            }}
          />
          {albumCollectionData.albums.length ? (
            <>
              <Button
                disabled={
                  !albumCollectionData.albums?.length ||
                  albumCollectionData.albums?.length <= 1
                }
                onClick={openAlbumOrderModal}
                style={{ width: 'fit-content' }}
              >
                Изменить порядок сезонов
              </Button>
              <Flex vertical gap="24px" style={{ marginTop: '24px' }}>
                {albumCollectionData.albums
                  ?.sort((a, b) => a.number - b.number)
                  .map((a) => (
                    <Flex key={a.id} vertical gap="24px">
                      <Flex justify="space-between">
                        <Typography.Title
                          editable={{
                            triggerType: ['icon', 'text'],
                            tooltip: 'Изменить название',
                            icon:
                              albumNameUpdateLatestParams?.id === a.id &&
                              albumNameUpdatePending ? (
                                <LoadingOutlined />
                              ) : (
                                <EditOutlined />
                              ),
                            onChange(value) {
                              if (value.trim() === a.name.trim()) return
                              updateAlbumName.start({
                                id: a.id,
                                data: {
                                  name: value,
                                },
                              })
                            },
                          }}
                          level={5}
                          style={{ margin: 0 }}
                        >
                          {a.name}
                        </Typography.Title>

                        <Tooltip title="Убрать из выпуска">
                          <Button
                            onClick={() => {
                              deleteAlbumFromAlbumCollection.start({
                                id: albumCollectionData.id,
                                params: {
                                  AlbumIds: [a.id],
                                },
                              })
                            }}
                            type="text"
                            shape="circle"
                            icon={<CloseOutlined />}
                          />
                        </Tooltip>
                      </Flex>
                      <Table
                        sticky
                        showSorterTooltip={false}
                        rowKey={(record) => record.id}
                        bordered
                        columns={tracksTableColumns}
                        pagination={false}
                        dataSource={tracks.data?.items.filter(
                          (t) => t.album?.albumId === a.id,
                        )}
                        locale={{
                          emptyText: tracks.pending ? (
                            <LoadingOutlined
                              style={{ fontSize: '48px', color: '#9254de' }}
                            />
                          ) : (
                            'В сезоне нет подкастов'
                          ),
                        }}
                      />
                    </Flex>
                  ))}
                <Button
                  onClick={openAlbumSelectionModal}
                  style={{ width: '100%' }}
                  icon={<PlusOutlined />}
                >
                  Добавить сезон
                </Button>
              </Flex>
            </>
          ) : (
            <EmptyContent
              description="В списке пока нет ни одного сезона"
              content={
                <Space>
                  <Button onClick={openAlbumSelectionModal}>
                    Добавить сезон
                  </Button>
                </Space>
              }
            />
          )}
        </>
      )}
    </>
  )
}
